import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { PlayerSelector, type PlayerSelectorHandle } from '@/components/PlayerSelector'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { apiClient } from '@/services/api'
import type { Player, Series, MatchOutcomeResult, MatchOutcomeResultOutcome, MatchResult } from '@/types/api'
import { toast } from 'sonner'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

interface MatchFormState {
  player_a_id: string
  player_b_id: string
  score_a: string
  score_b: string
  played_at_date: string
  played_at_time: string
  outcome: MatchOutcomeResultOutcome | ''
}

interface ReportMatchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  seriesId: string
  clubId?: string
  seriesStartDate?: string
  seriesEndDate?: string
  series?: Series  // Optional series object for advanced features
  onMatchReported: (match: { matchId: string; playedAt: string }) => void
}

export function ReportMatchDialog({
  open,
  onOpenChange,
  seriesId,
  clubId,
  seriesStartDate,
  seriesEndDate,
  series,
  onMatchReported
}: ReportMatchDialogProps) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const playerASelectorRef = useRef<PlayerSelectorHandle | null>(null)
  const isOutcomeScoring = series?.scoringProfile === 'SCORING_PROFILE_MATCH_OUTCOME'
  const isScorelineScoring = series?.scoringProfile === 'SCORING_PROFILE_SCORELINE'
  const isSetScoring = !isOutcomeScoring && !isScorelineScoring

  // Scoreline constants (for table hockey, foosball)
  const SCORELINE_MIN = 0
  const SCORELINE_MAX = 50

  const getSetsToPlay = () => series?.setsToPlay || 5
  const getRequiredWins = () => Math.ceil(getSetsToPlay() / 2)
  const getValidResultsText = () => {
    const requiredWins = getRequiredWins()
    return Array.from({ length: requiredWins }, (_, index) => `${requiredWins}-${index}`).join(', ')
  }

  // Helper function to validate table tennis scores
  const validateTableTennisScore = (scoreA: number, scoreB: number) => {
    const setsToPlay = getSetsToPlay()

    // Generate valid results based on setsToPlay
    const validResults: [number, number][] = []
    const requiredWins = Math.ceil(setsToPlay / 2)
    
    // Winner gets requiredWins, loser gets 0 to requiredWins-1
    for (let loserSets = 0; loserSets < requiredWins; loserSets++) {
      validResults.push([requiredWins, loserSets])
      validResults.push([loserSets, requiredWins])
    }
    
    return validResults.some(([a, b]) => a === scoreA && b === scoreB)
  }

  // Helper function to auto-complete score when one player has required wins and other is empty/0
  const getAutoCompletedScores = (scoreAStr: string, scoreBStr: string) => {
    const requiredWins = getRequiredWins()
    
    // Parse scores, treating empty strings and invalid numbers as 0
    const scoreA = scoreAStr === '' || isNaN(parseInt(scoreAStr, 10)) ? 0 : parseInt(scoreAStr, 10)
    const scoreB = scoreBStr === '' || isNaN(parseInt(scoreBStr, 10)) ? 0 : parseInt(scoreBStr, 10)
    
    // If one player has required wins and the other is 0 (or empty), auto-complete to a shutout
    if (scoreA === requiredWins && (scoreBStr === '' || scoreB === 0)) {
      return { scoreA: requiredWins, scoreB: 0, autoCompleted: true }
    }
    if (scoreB === requiredWins && (scoreAStr === '' || scoreA === 0)) {
      return { scoreA: 0, scoreB: requiredWins, autoCompleted: true }
    }
    
    return { scoreA, scoreB, autoCompleted: false }
  }
  const createEmptyFormState = (): MatchFormState => ({
    player_a_id: '',
    player_b_id: '',
    score_a: '0',
    score_b: '0',
    played_at_date: '',
    played_at_time: '',
    outcome: ''
  })

  const [formData, setFormData] = useState<MatchFormState>(() => createEmptyFormState())

  // Multi-match session state
  const [keepDialogOpen, setKeepDialogOpen] = useState(false)
  const [sessionCount, setSessionCount] = useState(0)

  const formatDatePart = (date: Date) => date.toISOString().split('T')[0]
  const formatTimePart = (date: Date) => date.toTimeString().slice(0, 5)
  const addMinutes = (date: Date, minutes: number) => new Date(date.getTime() + minutes * 60 * 1000)

  // Reset form when dialog opens/closes and prime initial date/time suggestion
  useEffect(() => {
    if (!open) {
      setFormData(createEmptyFormState())
      return
    }

    const now = new Date()
    setFormData(prev => ({
      ...prev,
      played_at_date: formatDatePart(now),
      played_at_time: formatTimePart(now),
      outcome: ''
    }))
  }, [open])

  useEffect(() => {
    if (!open) {
      return
    }

    const focusTimer = setTimeout(() => {
      playerASelectorRef.current?.focus()
    }, 0)

    // Keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC to close (unless in an input/select)
      if (e.key === 'Escape' && !['INPUT', 'SELECT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName)) {
        onOpenChange(false)
        setSessionCount(0)
        setKeepDialogOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      clearTimeout(focusTimer)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onOpenChange])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Only require players, date and time - scores will be auto-completed if needed
    if (!formData.player_a_id || !formData.player_b_id || !formData.played_at_date || !formData.played_at_time) {
      toast.error(t('form.validation.error'))
      return
    }

    if (formData.player_a_id === formData.player_b_id) {
      toast.error(t('matches.validation.samePlayer'))
      return
    }

    let scoreA = 0
    let scoreB = 0
    let matchOutcomeResult: MatchOutcomeResult | undefined

    if (isOutcomeScoring) {
      if (!formData.outcome) {
        toast.error(t('matches.validation.outcomeRequired'))
        return
      }
      matchOutcomeResult = { outcome: formData.outcome }
    } else if (isScorelineScoring) {
      // Scoreline scoring (table hockey, foosball) - validate 0-50 range
      scoreA = parseInt(formData.score_a, 10) || 0
      scoreB = parseInt(formData.score_b, 10) || 0

      if (scoreA < SCORELINE_MIN || scoreA > SCORELINE_MAX) {
        toast.error(t('matches.validation.scorelineRange', { min: SCORELINE_MIN, max: SCORELINE_MAX }))
        return
      }
      if (scoreB < SCORELINE_MIN || scoreB > SCORELINE_MAX) {
        toast.error(t('matches.validation.scorelineRange', { min: SCORELINE_MIN, max: SCORELINE_MAX }))
        return
      }
      // Draws allowed in open play, but may be rejected by backend for knockout formats
    } else {
      // Set-based scoring (table tennis, etc.)
      const autoResult = getAutoCompletedScores(formData.score_a, formData.score_b)
      scoreA = autoResult.scoreA
      scoreB = autoResult.scoreB

      if (!validateTableTennisScore(scoreA, scoreB)) {
        toast.error(t('matches.validation.invalidResult', { results: getValidResultsText() }))
        return
      }

      if (autoResult.autoCompleted) {
        setFormData(prev => ({
          ...prev,
          score_a: scoreA.toString(),
          score_b: scoreB.toString()
        }))
      }
    }

    // Validate that match date is within series time window (inclusive)
    if (seriesStartDate && seriesEndDate) {
      const matchDateTime = new Date(`${formData.played_at_date}T${formData.played_at_time}:00`)
      const seriesStart = new Date(seriesStartDate)
      const seriesEnd = new Date(seriesEndDate)
      
      // Set times to compare only dates (start of day for start, end of day for end)
      seriesStart.setHours(0, 0, 0, 0)
      seriesEnd.setHours(23, 59, 59, 999)
      
      if (matchDateTime < seriesStart || matchDateTime > seriesEnd) {
        toast.error(
          `${t('matches.validation.seriesWindow')} (${seriesStart.toLocaleDateString()} - ${seriesEnd.toLocaleDateString()})`
        )
        return
      }
    }

    try {
      setLoading(true)

      // Build match result payload based on scoring profile
      let matchResultPayload: MatchResult
      if (matchOutcomeResult) {
        matchResultPayload = { matchOutcome: matchOutcomeResult }
      } else if (isScorelineScoring) {
        matchResultPayload = { scoreline: { scoreA, scoreB, shootout: false } }
      } else {
        matchResultPayload = { tableTennis: { setsA: scoreA, setsB: scoreB } }
      }

      const reportRequest = {
        seriesId,
        participantA: { playerId: formData.player_a_id },
        participantB: { playerId: formData.player_b_id },
        matchResult: matchResultPayload,
        // Provide legacy fields until backend fully deprecates them
        playerAId: formData.player_a_id,
        playerBId: formData.player_b_id,
        scoreA,
        scoreB,
        playedAt: new Date(`${formData.played_at_date}T${formData.played_at_time}:00`).toISOString()
      }

      const response = await apiClient.reportMatch(reportRequest)
      
      // Update session tracking
      setSessionCount(prev => prev + 1)
      
      toast.success(t('matches.reported'))
      onMatchReported({
        matchId: response.matchId,
        playedAt: reportRequest.playedAt
      })

      if (keepDialogOpen) {
        // Prepare for next match - advance time by 5 minutes
        const nextSuggestedDate = addMinutes(new Date(reportRequest.playedAt), 5)
        setFormData({
          player_a_id: '',
          player_b_id: '',
          score_a: '0',
          score_b: '0',
          played_at_date: formatDatePart(nextSuggestedDate),
          played_at_time: formatTimePart(nextSuggestedDate),
          outcome: ''
        })

        setTimeout(() => {
          playerASelectorRef.current?.focus()
        }, 0)
      } else {
        // Close dialog and reset session
        onOpenChange(false)
        setSessionCount(0)
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : ''
      toast.error(message || t('error.generic'))
    } finally {
      setLoading(false)
    }
  }

  const handlePlayerASelected = (player: Player) => {
    setFormData(prev => ({ ...prev, player_a_id: player.id }))
  }

  const handlePlayerBSelected = (player: Player) => {
    setFormData(prev => ({ ...prev, player_b_id: player.id }))
  }

  const isFormValid = () => {
    // Basic required fields
    if (!formData.player_a_id || !formData.player_b_id || !formData.played_at_date || !formData.played_at_time) {
      return false
    }

    // Players must be different
    if (formData.player_a_id === formData.player_b_id) {
      return false
    }

    if (isOutcomeScoring) {
      return formData.outcome !== ''
    }

    if (isScorelineScoring) {
      // Scoreline: just check range 0-50
      const scoreA = parseInt(formData.score_a, 10)
      const scoreB = parseInt(formData.score_b, 10)
      return !isNaN(scoreA) && !isNaN(scoreB) &&
             scoreA >= SCORELINE_MIN && scoreA <= SCORELINE_MAX &&
             scoreB >= SCORELINE_MIN && scoreB <= SCORELINE_MAX
    }

    // Check if scores are valid (including auto-completion)
    const { scoreA, scoreB } = getAutoCompletedScores(formData.score_a, formData.score_b)
    return validateTableTennisScore(scoreA, scoreB)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] p-0">
        <div className="px-6 pt-6 pb-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              {t('matches.report')}
              {sessionCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {t('matches.multiMatch.sessionBadge', { count: sessionCount })}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-base mt-2">
              {t('matches.reportDescription', 'Report the result of a match')}
              {keepDialogOpen && (
                <div className="text-xs mt-2 p-2 bg-info-muted dark:bg-info/10 rounded-md text-info-muted-foreground dark:text-info-foreground">
                  ℹ️ {t('matches.multiMatch.modeActive')}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <DialogBody className="px-4 sm:px-6">
            <div className="space-y-6">
              {/* Player Selection Section */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  {t('common.players', 'Spelare')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      {series?.format === 'SERIES_FORMAT_LADDER' ? t('matches.challenger') : t('matches.player_a')} *
                    </Label>
                    <PlayerSelector
                      ref={playerASelectorRef}
                      value={formData.player_a_id}
                      onPlayerSelected={handlePlayerASelected}
                      clubId={clubId}
                      excludePlayerId={formData.player_b_id}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      {series?.format === 'SERIES_FORMAT_LADDER' ? t('matches.defender') : t('matches.player_b')} *
                    </Label>
                    <PlayerSelector
                      value={formData.player_b_id}
                      onPlayerSelected={handlePlayerBSelected}
                      clubId={clubId}
                      excludePlayerId={formData.player_a_id}
                    />
                  </div>
                </div>
              </div>

              {/* Result Section */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  ⚔️ {t('matches.outcome.label', 'Matchresultat')}
                </h3>
                {isOutcomeScoring ? (
                  <RadioGroup
                    value={formData.outcome}
                    onValueChange={(value) =>
                      setFormData(prev => ({ ...prev, outcome: value as MatchOutcomeResultOutcome }))
                    }
                    className="grid gap-2"
                  >
                    <div className="flex items-center space-x-3 rounded-md border-2 border-border hover:border-primary/50 bg-background p-3 transition-colors min-h-[44px]">
                      <RadioGroupItem value="OUTCOME_PLAYER_A_WIN" id="outcome_player_a" />
                      <Label htmlFor="outcome_player_a" className="flex-1 cursor-pointer font-medium">
                        {t('matches.outcome.playerAWins', { 
                          player: series?.format === 'SERIES_FORMAT_LADDER' ? t('matches.challenger') : t('matches.player_a')
                        })}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 rounded-md border-2 border-border hover:border-primary/50 bg-background p-3 transition-colors min-h-[44px]">
                      <RadioGroupItem value="OUTCOME_PLAYER_B_WIN" id="outcome_player_b" />
                      <Label htmlFor="outcome_player_b" className="flex-1 cursor-pointer font-medium">
                        {t('matches.outcome.playerBWins', { 
                          player: series?.format === 'SERIES_FORMAT_LADDER' ? t('matches.defender') : t('matches.player_b')
                        })}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 rounded-md border-2 border-border hover:border-primary/50 bg-background p-3 transition-colors min-h-[44px]">
                      <RadioGroupItem value="OUTCOME_DRAW" id="outcome_draw" />
                      <Label htmlFor="outcome_draw" className="flex-1 cursor-pointer font-medium">
                        {t('matches.outcome.draw')}
                      </Label>
                    </div>
                  </RadioGroup>
                ) : isScorelineScoring ? (
                  /* Scoreline scoring for table hockey, foosball (0-50 goals) */
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="score_a" className="text-sm font-medium">{t('matches.goals_a', 'Spelare A mål')}</Label>
                      <Input
                        id="score_a"
                        type="number"
                        min={SCORELINE_MIN}
                        max={SCORELINE_MAX}
                        value={formData.score_a}
                        onChange={(e) => setFormData(prev => ({ ...prev, score_a: e.target.value }))}
                        placeholder="0"
                        className="h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="score_b" className="text-sm font-medium">{t('matches.goals_b', 'Spelare B mål')}</Label>
                      <Input
                        id="score_b"
                        type="number"
                        min={SCORELINE_MIN}
                        max={SCORELINE_MAX}
                        value={formData.score_b}
                        onChange={(e) => setFormData(prev => ({ ...prev, score_b: e.target.value }))}
                        placeholder="0"
                        className="h-11"
                      />
                    </div>
                  </div>
                ) : (
                  /* Set-based scoring for table tennis, etc. */
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="score_a" className="text-sm font-medium">{t('matches.score_a')}</Label>
                      <Input
                        id="score_a"
                        type="number"
                        min="0"
                        max={getRequiredWins()}
                        value={formData.score_a}
                        onChange={(e) => setFormData(prev => ({ ...prev, score_a: e.target.value }))}
                        placeholder="0"
                        className="h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="score_b" className="text-sm font-medium">{t('matches.score_b')}</Label>
                      <Input
                        id="score_b"
                        type="number"
                        min="0"
                        max={getRequiredWins()}
                        value={formData.score_b}
                        onChange={(e) => setFormData(prev => ({ ...prev, score_b: e.target.value }))}
                        placeholder="0"
                        className="h-11"
                      />
                    </div>
                  </div>
                )}
                {isScorelineScoring && (
                  <p className="text-xs text-muted-foreground mt-2">
                    💡 {t('matches.validation.scorelineHint', 'Ange antal mål (0-50) för varje spelare')}
                  </p>
                )}
                {isSetScoring && (
                  <p className="text-xs text-muted-foreground mt-2">
                    💡 {t('matches.validation.validResults', { results: getValidResultsText() })}
                  </p>
                )}
              </div>

              {/* Date and Time Section */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  📅 {t('matches.played_at', 'Datum & Tid')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="played_at_date" className="text-sm font-medium flex items-center gap-1">
                      🗓️ {t('matches.played_at_date')} *
                    </Label>
                    <Input
                      id="played_at_date"
                      type="date"
                      value={formData.played_at_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, played_at_date: e.target.value }))}
                      required
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="played_at_time" className="text-sm font-medium flex items-center gap-1">
                      ⏰ {t('matches.played_at_time')} *
                    </Label>
                    <Input
                      id="played_at_time"
                      type="time"
                      value={formData.played_at_time}
                      onChange={(e) => setFormData(prev => ({ ...prev, played_at_time: e.target.value }))}
                      required
                      className="h-11"
                    />
                  </div>
                </div>
              </div>

              {/* Multi-match Settings */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Switch
                    id="keepOpen"
                    checked={keepDialogOpen}
                    onCheckedChange={setKeepDialogOpen}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <Label htmlFor="keepOpen" className="text-sm font-medium cursor-pointer">
                      {t('matches.multiMatch.keepOpen')}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('matches.multiMatch.timeAdvance')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Validation Rules */}
              <div className="bg-warning-muted dark:bg-warning/10 border border-warning/30 dark:border-warning/40 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-warning-foreground dark:text-warning-foreground mb-2 uppercase tracking-wide">
                  ⚠️ {t('common.rules', 'Regler')}
                </h4>
                <ul className="space-y-1.5 text-xs text-warning-foreground dark:text-warning-foreground">
                  {isOutcomeScoring ? (
                    <>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5">•</span>
                        <span>{t('matches.outcome.description')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5">•</span>
                        <span>{t('matches.validation.samePlayer')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5">•</span>
                        <span>{t('matches.validation.seriesWindow')}</span>
                      </li>
                    </>
                  ) : isScorelineScoring ? (
                    <>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5">•</span>
                        <span>{t('matches.validation.scorelineFormat', 'Ange antal mål (0-50) för varje spelare')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5">•</span>
                        <span>{t('matches.validation.drawsAllowedOpenPlay', 'Oavgjort tillåtet i öppet spel')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5">•</span>
                        <span>{t('matches.validation.samePlayer')}</span>
                      </li>
                    </>
                  ) : (
                    <>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5">•</span>
                        <span>{t('matches.validation.bestOfFormat', {
                          sets: getSetsToPlay(),
                          wins: getRequiredWins()
                        })}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5">•</span>
                        <span>{t('matches.validation.scoresRangeDynamic', { max: getRequiredWins() })}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5">•</span>
                        <span>{t('matches.validation.noTies')}</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5">•</span>
                        <span>{t('matches.validation.samePlayer')}</span>
                      </li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </DialogBody>

          <div className="px-4 sm:px-6 pb-6 pt-4 border-t bg-background">
            <DialogFooter className="gap-3">
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => {
                  onOpenChange(false)
                  setSessionCount(0)
                  setKeepDialogOpen(false)
                }}
                className="sm:flex-initial flex-1"
              >
                {keepDialogOpen ? t('matches.multiMatch.closeSession', 'Close Session') : t('common.cancel', 'Avbryt')}
              </Button>
              <Button 
                type="submit" 
                disabled={!isFormValid() || loading}
                className="sm:flex-initial flex-1 min-h-[44px]"
              >
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    ✅ {t('matches.report')}
                    {keepDialogOpen && sessionCount > 0 && (
                      <Badge variant="secondary" className="ml-2 text-xs">
                        #{sessionCount + 1}
                      </Badge>
                    )}
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

